import { Injectable } from "@angular/core";
import {
  ReframeKeyframe,
  ReframeConfig,
  ReframeCropRect,
} from "app/js/src/domain/clipping-studio/clipping-studio-project";
import {
  FocusScene,
  parseFocusStartTimeMs,
} from "app/js/src/domain/clipping-studio/clipping-studio-focus";
import { ClippingStudioClip } from "app/js/src/domain/clipping-studio/clipping-studio-clip";
import { AspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";

const AI_REFRAME_LAYOUT: ReframeConfig["layout"] = "fill";
const AI_REFRAME_BACKGROUND: ReframeConfig["backgroundStyle"] = "blur";

function parseAspectRatio(aspectRatio: AspectRatio): { w: number; h: number } {
  const parts = aspectRatio.trim().split(/\s*:\s*/);
  const w = parts.length >= 1 ? parseFloat(parts[0]) || 16 : 16;
  const h = parts.length >= 2 ? parseFloat(parts[1]) || 9 : 9;
  return { w, h };
}

/**
 * Minimum crop width (normalized 0-1) for target aspect so the crop fits without stretch.
 * Source 16:9, full height (1). For portrait (e.g. 9:16), min width = 9/16; for landscape, 1.
 */
function getMinCropWidth(aspectRatio: AspectRatio): number {
  const { w, h } = parseAspectRatio(aspectRatio);
  const targetRatio = w / h;
  return targetRatio <= 1 ? targetRatio : 1;
}

/**
 * Expands and clamps horizontal crop from focus box (TBLR).
 * Source 16:9, full height (top=0, bottom=1). Left/Right from focus_box.
 * If width < min (for target aspect), expand centered then clamp to [0,1].
 */
function focusBoxToCropRect(
  left: number,
  right: number,
  aspectRatio: AspectRatio,
): ReframeCropRect {
  let width = right - left;
  const minWidth = getMinCropWidth(aspectRatio);

  if (width < minWidth) {
    const center = (left + right) / 2;
    let newLeft = center - minWidth / 2;
    let newRight = center + minWidth / 2;
    if (newRight > 1) {
      const shift = newRight - 1;
      newLeft = Math.max(0, newLeft - shift);
      newRight = newRight - shift;
    }
    if (newLeft < 0) {
      const shift = -newLeft;
      newLeft = 0;
      newRight = Math.min(1, newRight + shift);
    }
    return { x: newLeft, y: 0, width: minWidth, height: 1 };
  }

  // Wider crop: use as-is, clamp to [0,1]
  let x = left;
  if (x < 0) {
    width = width + x;
    x = 0;
  }
  if (x + width > 1) {
    width = 1 - x;
  }
  return { x, y: 0, width, height: 1 };
}

@Injectable()
export class ClippingStudioAiReframeService {
  /**
   * Fetches and parses the focus JSON from the given URL.
   */
  async fetchFocusScenes(focusUrl: string): Promise<FocusScene[]> {
    const res = await fetch(focusUrl);
    if (!res.ok) throw new Error(`Focus fetch failed: ${res.status}`);
    const raw = await res.json();
    if (!Array.isArray(raw)) return [];
    return raw as FocusScene[];
  }

  /**
   * Computes reframe keyframes for one clip from focus scenes.
   * Only includes scenes whose start_time (ms) falls within [clip.inMs, clip.outMs].
   * Each keyframe uses layout customBackground, backgroundStyle blur, crop from focus_box (expand/clamp).
   */
  computeKeyframesForClip(
    clip: { inMs: number; outMs: number },
    scenes: FocusScene[],
    aspectRatio: AspectRatio,
  ): ReframeKeyframe[] {
    const clipStartMs = clip.inMs;
    const clipEndMs = clip.outMs;
    const keyframes: ReframeKeyframe[] = [];

    for (const scene of scenes) {
      const sceneMs = parseFocusStartTimeMs(scene.start_time);
      if (sceneMs < clipStartMs || sceneMs > clipEndMs) continue;

      const timeMs = sceneMs - clipStartMs;
      const box = scene.focus_box;
      let left = parseFloat(box.Left);
      let right = parseFloat(box.Right);
      if (Number.isNaN(left)) left = 0;
      if (Number.isNaN(right)) right = 1;
      if (right <= left) right = Math.min(1, left + 0.1);
      const crop = focusBoxToCropRect(left, right, aspectRatio);

      keyframes.push({
        timeMs,
        config: {
          layout: AI_REFRAME_LAYOUT,
          backgroundStyle: AI_REFRAME_BACKGROUND,
          crop,
        },
      });
    }

    keyframes.sort((a, b) => a.timeMs - b.timeMs);

    // Ensure at least one keyframe at 0
    if (keyframes.length === 0) {
      const minW = getMinCropWidth(aspectRatio);
      keyframes.push({
        timeMs: 0,
        config: {
          layout: AI_REFRAME_LAYOUT,
          backgroundStyle: AI_REFRAME_BACKGROUND,
          crop: { x: 0, y: 0, width: minW, height: 1 },
        },
      });
    } else if (keyframes[0].timeMs > 0) {
      keyframes.unshift({
        timeMs: 0,
        config: keyframes[0].config,
      });
    }

    return keyframes;
  }

  /**
   * Fetches focus once, then computes keyframes per destination and per clip.
   * Returns Record<destinationId, Record<clipId, ReframeKeyframe[]>>.
   */
  async applyForClips(
    clips: ClippingStudioClip[],
    focusUrl: string,
    destinationIdToAspectRatio: Record<string, AspectRatio>,
  ): Promise<Record<string, Record<string, ReframeKeyframe[]>>> {
    const result: Record<string, Record<string, ReframeKeyframe[]>> = {};
    let scenes: FocusScene[];
    try {
      scenes = await this.fetchFocusScenes(focusUrl);
    } catch {
      return result;
    }

    const destIds = Object.keys(destinationIdToAspectRatio);
    for (const destId of destIds) {
      const aspectRatio = destinationIdToAspectRatio[destId];
      result[destId] = {};
      for (const clip of clips) {
        result[destId][clip.id] = this.computeKeyframesForClip(
          clip,
          scenes,
          aspectRatio,
        );
      }
    }
    return result;
  }
}
